import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AlertModal from '../components/AlertModal';
import PremiumSelect from '../components/PremiumSelect';
import { AlertState, FinanceDashboardResponse, FinanceTransactionType } from '../types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [dashboard, setDashboard] = useState<FinanceDashboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedCycleId, setSelectedCycleId] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<'all' | FinanceTransactionType>('all');
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });

  const canAccess = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';

  const loadDashboardData = useCallback(async (): Promise<void> => {
    if (!user || !canAccess) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get<FinanceDashboardResponse>('/reports/dashboard', {
        params: {
          year: selectedYear !== 'all' ? selectedYear : undefined,
          cycleId: selectedCycleId !== 'all' ? selectedCycleId : undefined,
          type: selectedType !== 'all' ? selectedType : undefined,
        },
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setAlert({
        isOpen: true,
        message: 'Erro ao carregar o dashboard financeiro',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [canAccess, selectedCycleId, selectedType, selectedYear, user]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [loadDashboardData, user]);

  const yearSelectOptions = useMemo(() => {
    const years = dashboard?.filters.options.years || [];
    return [
      { value: 'all', label: 'Todos' },
      ...years.map((y) => ({ value: String(y), label: String(y) })),
    ];
  }, [dashboard]);

  const cycleSelectOptions = useMemo(() => {
    const cycles = dashboard?.filters.options.cycles || [];
    return [
      { value: 'all', label: 'Todos' },
      ...cycles.map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [dashboard]);

  const typeSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'income', label: 'Receitas' },
      { value: 'expense', label: 'Despesas' },
    ],
    [],
  );

  const firstName = useMemo(() => user?.name?.split(' ')[0] || 'Equipe', [user?.name]);
  const roleTitle = user?.role === 'manager' ? 'Painel da gestão' : 'Visão financeira consolidada';
  const roleDescription =
    user?.role === 'manager'
      ? 'Acompanhe receitas, despesas, categorias críticas e períodos da operação.'
      : 'Tenha uma leitura executiva do caixa, tendências e concentração dos lançamentos.';

  const formatDate = (value?: string): string => {
    if (!value) return 'Não definido';
    return new Date(value).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatPercent = (value: number | null): string =>
    value === null
      ? 'Sem base'
      : `${value.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`;

  if (loading) {
    return (
      <div className="dashboard-state">
        <div className="dashboard-state-card">
          <span className="dashboard-state-badge">Carregando</span>
          <h1>Preparando seu painel</h1>
          <p>Organizando indicadores, gráficos e tendências da operação financeira.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dashboard-state">
        <div className="dashboard-state-card">
          <span className="dashboard-state-badge">Sem dados</span>
          <h1>Nenhum dado disponível</h1>
          <p>Não encontramos informações para montar o dashboard neste momento.</p>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="dashboard-state">
        <div className="dashboard-state-card">
          <span className="dashboard-state-badge">Acesso</span>
          <h1>Painel restrito</h1>
          <p>O dashboard financeiro está disponível apenas para perfis de gestão, RH e administração.</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="dashboard-state">
        <div className="dashboard-state-card">
          <span className="dashboard-state-badge">Sem dados</span>
          <h1>Nenhum dado disponível</h1>
          <p>Não encontramos informações para montar o dashboard financeiro neste momento.</p>
        </div>
      </div>
    );
  }

  const expenseCategories = [...dashboard.expenseCategories].sort((a, b) => b.expense - a.expense);
  const incomeSources = [...dashboard.incomeSources].sort((a, b) => b.income - a.income);
  const comparisonLabel = dashboard.comparison.currentPeriodLabel || 'Período atual';
  const previousComparisonLabel = dashboard.comparison.previousPeriodLabel || 'Sem base anterior';
  const getComparisonTone = (
    metric: 'income' | 'expense' | 'balance' | 'transactionsCount',
  ): 'is-positive' | 'is-attention' | 'is-neutral' => {
    const difference = dashboard.comparison[metric].difference;
    if (difference === 0) {
      return 'is-neutral';
    }
    if (metric === 'expense') {
      return difference < 0 ? 'is-positive' : 'is-attention';
    }
    return difference > 0 ? 'is-positive' : 'is-attention';
  };

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <span className="dashboard-kicker">{roleTitle}</span>
          <h1>Olá, {firstName}</h1>
          <p>{roleDescription}</p>
        </div>

        <div className="dashboard-hero-side">
          <div className="dashboard-hero-card">
            <span className="dashboard-card-label">Período ativo</span>
            <strong>{dashboard.activeCycle?.name || 'Nenhum período ativo'}</strong>
            <p>
              {dashboard.activeCycle
                ? `Fechamento em ${formatDate(dashboard.activeCycle.deadlineDate)}`
                : 'Ative um ciclo para destacar o período financeiro principal.'}
            </p>
          </div>

          <div className="dashboard-quick-actions">
            <Link to="/financeiro" className="dashboard-action-button">
              Novo lançamento
            </Link>
            <Link to="/reports" className="dashboard-action-button is-secondary">
              Abrir relatórios
            </Link>
          </div>
        </div>
      </section>

      <section className="dashboard-surface">
        <div className="dashboard-section-header">
          <div>
            <span className="dashboard-section-kicker">Filtros</span>
            <h2>Refine o painel</h2>
          </div>
          <p>Use os filtros para destacar um ano, período ou apenas receitas e despesas.</p>
        </div>

        <div className="dashboard-filter-grid premium-filters">
          <PremiumSelect
            id="dashboard-year"
            label="Ano"
            wrapperClassName="premium-select-field"
            value={selectedYear}
            options={yearSelectOptions}
            onChange={setSelectedYear}
            placeholder="Todos"
          />

          <PremiumSelect
            id="dashboard-cycle"
            label="Período"
            wrapperClassName="premium-select-field"
            value={selectedCycleId}
            options={cycleSelectOptions}
            onChange={setSelectedCycleId}
            placeholder="Todos"
          />

          <PremiumSelect
            id="dashboard-type"
            label="Tipo"
            wrapperClassName="premium-select-field"
            value={selectedType}
            options={typeSelectOptions}
            onChange={(value) => setSelectedType(value as 'all' | FinanceTransactionType)}
            placeholder="Todos"
          />

          <button
            type="button"
            className="premium-filter-reset"
            onClick={() => {
              setSelectedYear('all');
              setSelectedCycleId('all');
              setSelectedType('all');
            }}
          >
            Limpar filtros
          </button>
        </div>
      </section>

      <section className="dashboard-stats-grid">
        <article className="dashboard-stat-card">
          <span>Receitas</span>
          <strong>{formatCurrency(dashboard.summary.totalIncome)}</strong>
          <p>Total de entradas no recorte atual.</p>
        </article>

        <article className="dashboard-stat-card">
          <span>Despesas</span>
          <strong>{formatCurrency(dashboard.summary.totalExpense)}</strong>
          <p>Total de saídas classificadas no recorte atual.</p>
        </article>

        <article className="dashboard-stat-card">
          <span>Saldo</span>
          <strong>{formatCurrency(dashboard.summary.balance)}</strong>
          <p>Resultado líquido entre receitas e despesas.</p>
        </article>

        <article className="dashboard-stat-card">
          <span>Lançamentos</span>
          <strong>{dashboard.summary.transactionsCount}</strong>
          <p>Quantidade total de lançamentos filtrados.</p>
        </article>
      </section>

      <section className="dashboard-surface">
        <div className="dashboard-section-header">
          <div>
            <span className="dashboard-section-kicker">Comparativo</span>
            <h2>{comparisonLabel} x {previousComparisonLabel}</h2>
          </div>
          <p>Variação absoluta e percentual entre o período atual e o imediatamente anterior.</p>
        </div>

        <div className="dashboard-comparison-grid">
          <article className={`dashboard-comparison-card ${getComparisonTone('income')}`}>
            <span>Receitas</span>
            <strong>{formatCurrency(dashboard.comparison.income.current)}</strong>
            <small>Anterior: {formatCurrency(dashboard.comparison.income.previous)}</small>
            <div className={`dashboard-comparison-delta ${getComparisonTone('income')}`}>
              <b>{dashboard.comparison.income.difference >= 0 ? '+' : ''}{formatCurrency(dashboard.comparison.income.difference)}</b>
              <span>{formatPercent(dashboard.comparison.income.percentChange)}</span>
            </div>
          </article>

          <article className={`dashboard-comparison-card ${getComparisonTone('expense')}`}>
            <span>Despesas</span>
            <strong>{formatCurrency(dashboard.comparison.expense.current)}</strong>
            <small>Anterior: {formatCurrency(dashboard.comparison.expense.previous)}</small>
            <div className={`dashboard-comparison-delta ${getComparisonTone('expense')}`}>
              <b>{dashboard.comparison.expense.difference >= 0 ? '+' : ''}{formatCurrency(dashboard.comparison.expense.difference)}</b>
              <span>{formatPercent(dashboard.comparison.expense.percentChange)}</span>
            </div>
          </article>

          <article className={`dashboard-comparison-card ${getComparisonTone('balance')}`}>
            <span>Saldo</span>
            <strong>{formatCurrency(dashboard.comparison.balance.current)}</strong>
            <small>Anterior: {formatCurrency(dashboard.comparison.balance.previous)}</small>
            <div className={`dashboard-comparison-delta ${getComparisonTone('balance')}`}>
              <b>{dashboard.comparison.balance.difference >= 0 ? '+' : ''}{formatCurrency(dashboard.comparison.balance.difference)}</b>
              <span>{formatPercent(dashboard.comparison.balance.percentChange)}</span>
            </div>
          </article>

          <article className={`dashboard-comparison-card ${getComparisonTone('transactionsCount')}`}>
            <span>Lançamentos</span>
            <strong>{dashboard.comparison.transactionsCount.current}</strong>
            <small>Anterior: {dashboard.comparison.transactionsCount.previous}</small>
            <div className={`dashboard-comparison-delta ${getComparisonTone('transactionsCount')}`}>
              <b>{dashboard.comparison.transactionsCount.difference >= 0 ? '+' : ''}{dashboard.comparison.transactionsCount.difference}</b>
              <span>{formatPercent(dashboard.comparison.transactionsCount.percentChange)}</span>
            </div>
          </article>
        </div>
      </section>

      <section className="dashboard-content-grid dashboard-content-grid-wide">
        <article className="dashboard-surface dashboard-chart-surface">
          <div className="dashboard-section-header">
            <div>
              <span className="dashboard-section-kicker">Evolução</span>
              <h2>Receitas e despesas por período</h2>
            </div>
          </div>

          {dashboard.trends.length > 0 ? (
            <div className="dashboard-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={dashboard.trends}>
                  <defs>
                    <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#16a34a" stopOpacity={0.34} />
                      <stop offset="95%" stopColor="#16a34a" stopOpacity={0.04} />
                    </linearGradient>
                    <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <Tooltip cursor={false} formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Area type="monotone" dataKey="income" name="Receitas" stroke="#16a34a" fill="url(#incomeFill)" strokeWidth={3} />
                  <Area type="monotone" dataKey="expense" name="Despesas" stroke="#dc2626" fill="url(#expenseFill)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="dashboard-empty-block">
              <p>Nenhum dado encontrado para montar a série por período.</p>
            </div>
          )}
        </article>

        <article className="dashboard-surface">
          <div className="dashboard-section-header">
            <div>
              <span className="dashboard-section-kicker">Maiores lançamentos</span>
              <h2>Top movimentações</h2>
            </div>
            <Link to="/reports" className="dashboard-inline-link">
              Abrir relatório
            </Link>
          </div>

          <div className="dashboard-feedback-list">
            {dashboard.topTransactions.length > 0 ? (
              dashboard.topTransactions.map((transaction) => (
                <div className="dashboard-feedback-item" key={transaction.id}>
                  <div>
                    <strong>{transaction.description}</strong>
                    <span>
                      {formatDate(transaction.transactionDate)} • {transaction.categoryName}
                    </span>
                  </div>

                  <div className="dashboard-feedback-meta">
                    <span
                      className={`dashboard-finance-pill ${
                        transaction.type === 'income' ? 'is-positive' : 'is-attention'
                      }`}
                    >
                      {formatCurrency(transaction.amount)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="dashboard-empty-block">
                <p>Sem lançamentos suficientes para destacar maiores movimentações.</p>
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="dashboard-content-grid">
        <article className="dashboard-surface dashboard-chart-surface">
          <div className="dashboard-section-header">
            <div>
              <span className="dashboard-section-kicker">Despesas</span>
              <h2>Categorias com maior peso</h2>
            </div>
          </div>

          {expenseCategories.length > 0 ? (
            <div className="dashboard-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={expenseCategories}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <Tooltip cursor={false} formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="expense" name="Despesas" fill="#dc2626" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="dashboard-empty-block">
              <p>As categorias com maior peso aparecerão quando houver despesas no filtro atual.</p>
            </div>
          )}
        </article>

        <article className="dashboard-surface">
          <div className="dashboard-section-header">
            <div>
              <span className="dashboard-section-kicker">Receitas</span>
              <h2>Fontes com maior entrada</h2>
            </div>
          </div>

          {incomeSources.length > 0 ? (
            <div className="dashboard-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={incomeSources}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <Tooltip cursor={false} formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="income" name="Receitas" fill="#16a34a" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="dashboard-empty-block">
              <p>As fontes de receita aparecerão quando houver entradas no filtro atual.</p>
            </div>
          )}
        </article>
      </section>

      <section className="dashboard-surface">
        <div className="dashboard-section-header">
          <div>
            <span className="dashboard-section-kicker">Lançamentos recentes</span>
            <h2>Últimas movimentações</h2>
          </div>
          <Link to="/reports" className="dashboard-inline-link">
            Ver relatório completo
          </Link>
        </div>

        {dashboard.recentTransactions.length > 0 ? (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th>Tipo</th>
                  <th>Período</th>
                  <th>Categoria</th>
                  <th>Fonte</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recentTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.transactionDate)}</td>
                    <td>{transaction.description}</td>
                    <td>{transaction.type === 'income' ? 'Receita' : 'Despesa'}</td>
                    <td>{transaction.period}</td>
                    <td>{transaction.categoryName}</td>
                    <td>{transaction.sourceName}</td>
                    <td>{formatCurrency(transaction.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dashboard-empty-block">
            <p>Não há lançamentos recentes no recorte atual.</p>
          </div>
        )}
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert((current) => ({ ...current, isOpen: false }))}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default Dashboard;
